import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import { IStudent } from './types';
import { stripReasonTag } from './format';

interface IContactCardProps {
  student: IStudent;
}

/**
 * Read-only contact card — left column of the Overview dashboard (Area 4,
 * Req 12). Always shows email AND phone together with a localized empty
 * indicator for whichever is missing (Req 12.1), plus a notes section from the
 * agreement `comment` (empty when blank) (Req 12.2). Profile fields registration
 * date + acquisition source are shown with an empty indicator when null (Req
 * 12.3, TZ-2). `responsibleManager` is intentionally OMITTED — the backend
 * returns it always `null` in this version (no per-student source yet), so per
 * Req 12.4 it is not rendered as a placeholder.
 */
export const ContactCard = memo(({ student }: IContactCardProps) => {
  const { t } = useTranslation();
  const { user } = student;
  const email = user?.email;
  const phone = user?.phone;
  const registrationDate = student.registrationDate
    ? moment(student.registrationDate).format('DD.MM.YYYY')
    : '';
  const acquisitionSource = student.acquisitionSource || '';

  // First non-empty agreement comment; the notes section is always shown
  // because `comment` is available on the projection (Req 12.2). The backend
  // inlines the termination reason as a `[reason=...]` tag in `comment`, so it
  // is stripped before display so the machine tag never leaks to the operator.
  const note = useMemo(() => {
    const found = (student.agreements || [])
      .map((a) => stripReasonTag(a?.comment))
      .find((c) => c !== '');
    return found || '';
  }, [student.agreements]);

  return (
    <S.Card>
      <S.CardTitle>
        {t('pages.studentDetail.overview.contactSection')}
      </S.CardTitle>
      <S.Row>
        <S.RowLabel>{t('pages.studentDetail.overview.email')}</S.RowLabel>
        <S.RowValue>
          {email ? <a href={`mailto:${email}`}>{email}</a> : '—'}
        </S.RowValue>
      </S.Row>
      <S.Row>
        <S.RowLabel>{t('pages.studentDetail.overview.phone')}</S.RowLabel>
        <S.RowValue>
          {phone ? <a href={`tel:${phone}`}>{phone}</a> : '—'}
        </S.RowValue>
      </S.Row>
      <S.Row>
        <S.RowLabel>
          {t('pages.studentDetail.overview.registrationDate')}
        </S.RowLabel>
        <S.RowValue>{registrationDate || '—'}</S.RowValue>
      </S.Row>
      <S.Row>
        <S.RowLabel>
          {t('pages.studentDetail.overview.acquisitionSource')}
        </S.RowLabel>
        <S.RowValue>{acquisitionSource || '—'}</S.RowValue>
      </S.Row>
      <S.Row>
        <S.RowLabel>{t('pages.studentDetail.overview.notes')}</S.RowLabel>
        <S.RowValue>{note || '—'}</S.RowValue>
      </S.Row>
    </S.Card>
  );
});
